import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardPage.css';
import ApiService from '../services/api';

// ─── Skeleton Loader ───────────────────────────────────────────────
function SkeletonBlock({ width = '100%', height = '20px', style = {} }) {
  return (
    <div
      className="skeleton-block"
      style={{ width, height, borderRadius: '6px', ...style }}
    />
  );
}

function StatCardSkeleton() {
  return (
    <div className="dashboard-card skeleton-pulse">
      <div className="card-header">
        <SkeletonBlock width="24px" height="24px" />
        <SkeletonBlock width="80px" height="14px" style={{ marginLeft: '10px' }} />
      </div>
      <div className="card-content">
        <SkeletonBlock width="60px" height="36px" style={{ margin: '8px auto' }} />
        <SkeletonBlock width="100px" height="12px" style={{ margin: '4px auto' }} />
      </div>
    </div>
  );
}

// ─── Progress Bar ──────────────────────────────────────────────────
function ProgressBar({ segments }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  return (
    <div className="progress-bar-container">
      {segments.map((seg, i) => (
        <div
          key={seg.label}
          className={`progress-segment ${seg.color}`}
          style={{ width: `${(seg.value / total) * 100}%` }}
          title={`${seg.label}: ${seg.value}`}
        />
      ))}
    </div>
  );
}

// ─── Activity Timeline ─────────────────────────────────────────────
function ActivityTimeline({ terminals, loading }) {
  if (loading) {
    return (
      <div className="timeline-list">
        {[1, 2, 3].map(i => (
          <div key={i} className="timeline-item skeleton-pulse">
            <SkeletonBlock width="32px" height="32px" style={{ borderRadius: '50%' }} />
            <div style={{ flex: 1 }}>
              <SkeletonBlock width="70%" height="14px" />
              <SkeletonBlock width="40%" height="12px" style={{ marginTop: '6px' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!terminals || terminals.length === 0) {
    return (
      <div className="timeline-empty">
        <span className="timeline-empty-icon">📭</span>
        <p>No recent activity</p>
        <span className="timeline-empty-sub">Create your first terminal to get started</span>
      </div>
    );
  }

  const statusConfig = {
    completed: { icon: '✅', color: 'var(--color-success)' },
    in_progress: { icon: '🔄', color: 'var(--color-warning)' },
    pending_qa: { icon: '🔍', color: 'var(--color-info)' },
    problem: { icon: '❌', color: 'var(--color-danger)' },
    assigned: { icon: '📋', color: 'var(--color-primary)' },
  };

  return (
    <div className="timeline-list">
      {terminals.slice(0, 6).map((terminal, idx) => {
        const cfg = statusConfig[terminal.status] || statusConfig.assigned;
        return (
          <div key={terminal.id} className="timeline-item" style={{ animationDelay: `${idx * 0.05}s` }}>
            <div className="timeline-dot" style={{ background: cfg.color }}>
              <span>{cfg.icon}</span>
            </div>
            <div className="timeline-content">
              <p className="timeline-title">
                {terminal.street} {terminal.house_number} - {terminal.city}
              </p>
              <p className="timeline-meta">
                {terminal.status?.replace('_', ' ').toUpperCase()}
                {terminal.project_code && ` · ${terminal.project_code}`}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Quick Actions ─────────────────────────────────────────────────
function QuickActions({ role }) {
  const navigate = useNavigate();

  const actions = [
    { label: 'New Terminal', icon: '➕', color: '#4CAF50', onClick: () => navigate('/tasks') },
    { label: 'Calendar', icon: '📅', color: '#2196F3', onClick: () => navigate('/calendar') },
    { label: 'Tracking', icon: '📍', color: '#FF9800', onClick: () => navigate('/tracking') },
    { label: 'Profile', icon: '👤', color: '#9C27B0', onClick: () => navigate('/profile') },
  ];

  // Role-specific actions
  if (role === 'manager' || role === 'admin') {
    actions.splice(1, 0, { label: 'Export', icon: '📤', color: '#607D8B', onClick: () => navigate('/tasks') });
  }

  // Admin-only: User Management button
  if (role === 'admin') {
    actions.push({ label: 'Admin Panel', icon: '🛡️', color: '#E53935', onClick: () => navigate('/admin/users') });
  }

  return (
    <div className="quick-actions">
      {actions.map(action => (
        <button
          key={action.label}
          className="quick-action-btn"
          onClick={action.onClick}
          style={{ '--btn-color': action.color }}
        >
          <span className="quick-action-icon">{action.icon}</span>
          <span className="quick-action-label">{action.label}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Status Distribution ───────────────────────────────────────────
function StatusDistribution({ stats, loading }) {
  if (loading) {
    return (
      <div className="distribution-list">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="distribution-row skeleton-pulse">
            <SkeletonBlock width="80px" height="14px" />
            <SkeletonBlock width="100%" height="8px" style={{ borderRadius: '4px' }} />
            <SkeletonBlock width="40px" height="14px" />
          </div>
        ))}
      </div>
    );
  }

  if (!stats || stats.total === 0) {
    return <div className="distribution-empty">No data available yet</div>;
  }

  const segments = [
    { label: 'Completed', value: stats.completed || 0, color: 'segment-completed' },
    { label: 'In Progress', value: stats.in_progress || 0, color: 'segment-progress' },
    { label: 'Pending QA', value: stats.pending_qa || 0, color: 'segment-qa' },
    { label: 'Problem', value: stats.problem || 0, color: 'segment-problem' },
    { label: 'Assigned', value: stats.assigned || 0, color: 'segment-assigned' },
  ].filter(s => s.value > 0);

  const total = segments.reduce((s, seg) => s + seg.value, 0);

  return (
    <div className="distribution-list">
      {segments.map(seg => {
        const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0;
        return (
          <div key={seg.label} className="distribution-row">
            <div className="distribution-label">
              <span className={`distribution-dot ${seg.color}`} />
              <span>{seg.label}</span>
            </div>
            <div className="distribution-bar-track">
              <div className={`distribution-bar-fill ${seg.color}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="distribution-value">{seg.value} ({pct}%)</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Notifications Widget ──────────────────────────────────────────
function NotificationsWidget({ notifications, loading }) {
  if (loading) {
    return (
      <div className="notif-list">
        {[1, 2].map(i => (
          <div key={i} className="notif-item skeleton-pulse">
            <SkeletonBlock width="32px" height="32px" style={{ borderRadius: '50%' }} />
            <div style={{ flex: 1 }}>
              <SkeletonBlock width="80%" height="14px" />
              <SkeletonBlock width="50%" height="12px" style={{ marginTop: '4px' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!notifications || notifications.length === 0) {
    return <div className="notif-empty">No notifications</div>;
  }

  return (
    <div className="notif-list">
      {notifications.slice(0, 3).map(notif => (
        <div key={notif.id} className="notif-item">
          <div className="notif-icon">🔔</div>
          <div className="notif-content">
            <p className="notif-title">{notif.title || notif.message}</p>
            <p className="notif-time">
              {notif.created_at ? new Date(notif.created_at).toLocaleString() : ''}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Dashboard Component ──────────────────────────────────────
function DashboardPage({ user }) {
  const [stats, setStats] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [terminalStats, terminalList, notifs] = await Promise.all([
          ApiService.getTerminalStats('DEFAULT').catch(() => null),
          ApiService.listTerminals({ limit: '10' }).catch(() => []),
          ApiService.getNotifications().catch(() => []),
        ]);
        setStats(terminalStats);
        setTerminals(terminalList);
        setNotifications(notifs);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // The API may return null for empty lists; normalize before use.
  const terminalList = Array.isArray(terminals) ? terminals : [];
  const notifList = Array.isArray(notifications) ? notifications : [];

  const totalActive = stats
    ? (stats.assigned || 0) + (stats.in_progress || 0) + (stats.pending_qa || 0)
    : terminalList.length;

  const completionRate = stats && stats.total > 0
    ? Math.round((stats.completed / stats.total) * 100)
    : 0;

  const role = user?.role || 'default';

  return (
    <div className="dashboard-container">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="dashboard-header fade-in">
        <div className="header-top">
          <div>
            <h1>Welcome, {user?.first_name || user?.firstName || 'User'}!</h1>
            <p className="header-date">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
          <div className="header-role-badge">
            {role === 'admin' && '🛡️ Admin'}
            {role === 'manager' && '📋 Manager'}
            {role === 'worker' && '🔧 Worker'}
            {role === 'default' && '👤 User'}
          </div>
        </div>
      </header>

      {/* ── Stats Grid ─────────────────────────────────────────── */}
      <section className="dashboard-grid slide-up">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <div className="dashboard-card stat-card">
              <div className="card-header">
                <span className="card-icon">📋</span>
                <h3>Total Terminals</h3>
              </div>
              <div className="card-content">
                <p className="big-number">{stats?.total || terminalList.length}</p>
                <p className="card-description">In project</p>
              </div>
            </div>

            <div className="dashboard-card stat-card">
              <div className="card-header">
                <span className="card-icon">⚡</span>
                <h3>Active</h3>
              </div>
              <div className="card-content">
                <p className="big-number">{totalActive}</p>
                <p className="card-description">Assigned / In progress / Pending QA</p>
              </div>
            </div>

            <div className="dashboard-card stat-card">
              <div className="card-header">
                <span className="card-icon">✅</span>
                <h3>Completed</h3>
              </div>
              <div className="card-content">
                <p className="big-number">{stats?.completed || 0}</p>
                <p className="card-description">Approved terminals</p>
              </div>
            </div>

            <div className="dashboard-card stat-card">
              <div className="card-header">
                <span className="card-icon">📊</span>
                <h3>Completion Rate</h3>
              </div>
              <div className="card-content">
                <p className="big-number">{completionRate}%</p>
                <p className="card-description">Overall</p>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ── Progress Bar ───────────────────────────────────────── */}
      {!loading && stats && (
        <section className="widget-section slide-up">
          <ProgressBar
            segments={[
              { label: 'Completed', value: stats.completed || 0, color: 'segment-completed' },
              { label: 'In Progress', value: stats.in_progress || 0, color: 'segment-progress' },
              { label: 'Pending QA', value: stats.pending_qa || 0, color: 'segment-qa' },
              { label: 'Problem', value: stats.problem || 0, color: 'segment-problem' },
              { label: 'Assigned', value: stats.assigned || 0, color: 'segment-assigned' },
            ]}
          />
          <div className="progress-legend">
            <span><span className="legend-dot segment-completed" /> Completed</span>
            <span><span className="legend-dot segment-progress" /> In Progress</span>
            <span><span className="legend-dot segment-qa" /> Pending QA</span>
            <span><span className="legend-dot segment-problem" /> Problem</span>
            <span><span className="legend-dot segment-assigned" /> Assigned</span>
          </div>
        </section>
      )}

      {/* ── Quick Actions ──────────────────────────────────────── */}
      <section className="widget-section slide-up">
        <div className="widget-header">
          <h2>Quick Actions</h2>
        </div>
        <QuickActions role={role} />
      </section>

      {/* ── Two-column layout ──────────────────────────────────── */}
      <div className="dashboard-columns">
        {/* Activity Timeline */}
        <section className="widget-section slide-up">
          <div className="widget-header">
            <h2>Activity Timeline</h2>
            <span className="widget-badge">{terminalList.length} recent</span>
          </div>
          <ActivityTimeline terminals={terminalList} loading={loading} />
        </section>

        {/* Status Distribution */}
        <section className="widget-section slide-up">
          <div className="widget-header">
            <h2>Status Distribution</h2>
          </div>
          <StatusDistribution stats={stats} loading={loading} />
        </section>
      </div>

      {/* ── Notifications ──────────────────────────────────────── */}
      {notifList.length > 0 && (
        <section className="widget-section slide-up">
          <div className="widget-header">
            <h2>Notifications</h2>
            <span className="widget-badge">{notifList.length} new</span>
          </div>
          <NotificationsWidget notifications={notifList} loading={loading} />
        </section>
      )}

      {/* Spacer for bottom nav */}
      <div style={{ height: '80px' }}></div>
    </div>
  );
}

export default DashboardPage;